import React from 'react';
import { Grid, Paper, Typography, Box, withStyles, Card, CardContent } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import PipelineBuildBarChart from '../PipelineReviewPackage/PipelineBuildBarChart';
import CumulativePipelineBuildLineChart from '../PipelineReviewPackage/CumulativePipelineBuildLineChart';
import { PipelineByStageChart } from '../RegionalSalesReviewPackage/DealProgressionCharts';
import { PipelineAgingChart } from '../RegionalSalesReviewPackage/DealConversionCharts';
import * as d3 from 'd3';
import { updatedScheme10 } from '../../util/chartColorScheme';

const styles = (theme) => ({
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
    color: '#333',
  },
  updateFrequency: {
    backgroundColor: '#00bcd4',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 500,
    marginLeft: theme.spacing(2),
  },
  chartCard: {
    height: '100%',
    position: 'relative',
  },
  chartContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  legendContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
  },
  legend: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(2),
  },
  legendBox: {
    width: 12,
    height: 12,
    marginRight: theme.spacing(1),
    borderRadius: 2,
  },
});

const RegionalRSMPerformancePipelineOverview = (props) => {
  const { classes, data } = props;

  const isLoading = data === "";
  const hasData = !!data && data !== "" && data !== null;

  const normalizeRepCharts = (raw) => {
    if (!raw || typeof raw !== 'object') return null;
    return {
      pipelineBuild: raw.pipelineBuild?.data || [],
      pipelineBuildTargetLine: raw.pipelineBuild?.targetLine || null,
      cumulativePipeline: raw.cumulativePipeline?.data || [],
      pipelineByStage: raw.pipelineByStage?.data || [],
      stageCategories: raw.pipelineByStage?.stageCategories || [],
      currentFiscalQuarter: raw.pipelineByStage?.currentFiscalQuarter || null,
      ageBuckets: raw.pipelineAging?.ageBuckets || [],
      pipelineAging: raw.pipelineAging?.data || [],
      titles: {
        pipelineBuild: raw.pipelineBuild?.title || '',
        cumulativePipeline: raw.cumulativePipeline?.title || ''
      }
    };
  };

  const content = hasData ? normalizeRepCharts(data) : null;

  return (
    <Paper className={classes.paper}>
      <div className={classes.header}>
        <Box display="flex" flexDirection='column' alignItems="center">
          <Typography variant="h5" className={classes.title}>
            Pipeline Overview
          </Typography>
          <span className={classes.updateFrequency}>
            {'Live Data'}
          </span>
        </Box>
      </div>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card className={classes.chartCard}>
            <CardContent>
              <div className={classes.chartContainer}>
                {isLoading ? (
                  <Skeleton variant="rect" animation="wave" height={250} width="100%" />
                ) : !content || !content.pipelineBuild?.length ? (
                  <Typography variant="body2" color="textSecondary">No data available</Typography>
                ) : (
                  <PipelineBuildBarChart
                    data={content.pipelineBuild}
                    title={content.titles?.pipelineBuild}
                    targetLine={content.pipelineBuildTargetLine}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card className={classes.chartCard}>
            <CardContent>
              <div className={classes.chartContainer}>
                {isLoading ? (
                  <Skeleton variant="rect" animation="wave" height={250} width="100%" />
                ) : !content || !content.cumulativePipeline?.length ? (
                  <Typography variant="body2" color="textSecondary">No data available</Typography>
                ) : (
                  <CumulativePipelineBuildLineChart
                    data={content.cumulativePipeline}
                    title={content.titles?.cumulativePipeline}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card className={classes.chartCard}>
            <CardContent>
              <div className={classes.chartContainer}>
                {isLoading ? (
                  <Skeleton variant="rect" animation="wave" height={250} width="100%" />
                ) : !content || !content.pipelineByStage?.length ? (
                  <Typography variant="body2" color="textSecondary">No data available</Typography>
                ) : (
                  <PipelineByStageChart
                    data={content.pipelineByStage}
                    currentFiscalQuarter={content.currentFiscalQuarter}
                    stageCategories={content.stageCategories}
                  />
                )}
              </div>
              {!isLoading && (
                <div className={classes.legendContainer}>
                  <div className={classes.legend}>
                    {(() => {
                      const colorScale = d3.scaleOrdinal(updatedScheme10);
                      return (content?.stageCategories || []).map((category) => (
                        <div key={category} className={classes.legendItem}>
                          <div
                            className={classes.legendBox}
                            style={{ backgroundColor: colorScale(category) }}
                          />
                          <Typography variant="body2">{category}</Typography>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card className={classes.chartCard}>
            <CardContent>
              <div className={classes.chartContainer}>
                {isLoading ? (
                  <Skeleton variant="rect" animation="wave" height={250} width="100%" />
                ) : !content || !content.pipelineAging?.length ? (
                  <Typography variant="body2" color="textSecondary">No data available</Typography>
                ) : (
                  <PipelineAgingChart
                    data={content.pipelineAging}
                    ageBuckets={content.ageBuckets}
                    hideLegend
                  />
                )}
              </div>
              {!isLoading && (
                <div className={classes.legendContainer}>
                  <div className={classes.legend}>
                    {(() => {
                      const colorScale = d3.scaleOrdinal(updatedScheme10);
                      return (content?.ageBuckets || []).map((bucket) => (
                        <div key={bucket} className={classes.legendItem}>
                          <div
                            className={classes.legendBox}
                            style={{ backgroundColor: colorScale(bucket) }}
                          />
                          <Typography variant="body2">{bucket}</Typography>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default withStyles(styles)(RegionalRSMPerformancePipelineOverview);
